'use client';

import { useState, useCallback } from 'react';
import type { Chat, ChatMessage } from '../types';

interface UseChatOptions {
  apiBaseUrl?: string;
}

export function useChat(chatId: string, options: UseChatOptions = {}) {
  const { apiBaseUrl = '/api/meridian' } = options;
  const [chat, setChat] = useState<Chat | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadChat = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiBaseUrl}/chats?chatId=${encodeURIComponent(chatId)}`, {
        credentials: 'include',
      });
      const data = await response.json();
      if (data.success) {
        setChat(data.data);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load chat');
    } finally {
      setIsLoading(false);
    }
  }, [chatId, apiBaseUrl]);

  const updateTitle = useCallback(
    async (title: string) => {
      try {
        const response = await fetch(`${apiBaseUrl}/chats?chatId=${encodeURIComponent(chatId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title }),
          credentials: 'include',
        });
        const data = await response.json();
        if (data.success && chat) {
          setChat({ ...chat, title: data.data.title });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update chat');
      }
    },
    [chatId, apiBaseUrl, chat]
  );

  return { chat, isLoading, error, loadChat, updateTitle };
}
